const isChesapeakeTheme = document.querySelector('body[data-theme-variant="chesapeake"]');

const SHOPIFY_CONFIG = {
  storefrontAccessToken: isChesapeakeTheme ? '48a8c8634314973cc3219af2dd69c47f' : '92319d6e9368255090ebb64b4af840ac',
  apiVersion: '2025-04',
};

const STOREFRONT_PRODUCT_QUERY = `
  query getProducts($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Product {
        id
        metafield(namespace: "magento", key: "badges") {
          value
          type
        }
        backOrderDate: metafield(namespace: "magento", key: "back_order_date") {
          value
        }
        backOrderMessage: metafield(namespace: "magento", key: "back_order_message") {
          value
        }
        handle
        title
        totalInventory
        priceRange {
          minVariantPrice {
            amount
            currencyCode
          }
          maxVariantPrice {
            amount
            currencyCode
          }
        }
        compareAtPriceRange {
          minVariantPrice {
            amount
            currencyCode
          }
          maxVariantPrice {
            amount
            currencyCode
          }
        }
        variants(first: 10) {
          edges {
            node {
              id
              sku
              price {
                amount
                currencyCode
              }
              compareAtPrice {
                amount
                currencyCode
              }
              quantityAvailable
              availableForSale
            }
          }
        }
      }
    }
  }
`;

const STOREFRONT_PRODUCT_BY_HANDLE_QUERY = `
  query getProductByHandle($handle: String!) {
    product(handle: $handle) {
      id
      metafield(namespace: "magento", key: "badges") {
        value
        type
      }
      backOrderDate: metafield(namespace: "magento", key: "back_order_date") {
        value
      }
      backOrderMessage: metafield(namespace: "magento", key: "back_order_message") {
        value
      }
      handle
      title
      totalInventory
      priceRange {
        minVariantPrice {
          amount
          currencyCode
        }
        maxVariantPrice {
          amount
          currencyCode
        }
      }
      compareAtPriceRange {
        minVariantPrice {
          amount
          currencyCode
        }
        maxVariantPrice {
          amount
          currencyCode
        }
      }
      variants(first: 10) {
        edges {
          node {
            id
            sku
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
            quantityAvailable
            availableForSale
          }
        }
      }
    }
  }
`;

  const STOREFRONT_PRODUCT_BY_VARIANT_IDS_QUERY = `
    query getProductsByVariantIds($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on ProductVariant {
          id
          product {
            id
            metafield(namespace: "magento", key: "badges") {
              value
              type
            }
            handle
            title
          }
        }
      }
    }
  `;
  
let pricingMutationObserver;
let currentPricingHandlers = [];
let initCheckInterval;
let lastContainerClassState = '';
let lastDropdownLabelValue = '';
let hasInitializedPricing = false;

async function fetchProductDataByHandle(handle) {
  const url = `/api/${SHOPIFY_CONFIG.apiVersion}/graphql.json`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': SHOPIFY_CONFIG.storefrontAccessToken,
      },
      body: JSON.stringify({
        query: STOREFRONT_PRODUCT_BY_HANDLE_QUERY,
        variables: {
          handle: handle,
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data.errors) {
      return null;
    }

    const product = data.data.product || null;
    return product;
  } catch (error) {
    return null;
  }
}

async function fetchProductData(productId) {
  const url = `/api/${SHOPIFY_CONFIG.apiVersion}/graphql.json`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': SHOPIFY_CONFIG.storefrontAccessToken,
      },
      body: JSON.stringify({
        query: STOREFRONT_PRODUCT_QUERY,
        variables: {
          ids: [productId],
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data.errors) {
      console.error('GraphQL errors:', data.errors);
      return null;
    }

    const product = data.data.nodes[0] || null;
    return product;
  } catch (error) {
    console.error('Error fetching product data:', error);
    return null;
  }
}

async function fetchProductDataByVariantIds(variantIds) {
  const url = `/api/${SHOPIFY_CONFIG.apiVersion}/graphql.json`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': SHOPIFY_CONFIG.storefrontAccessToken,
      },
      body: JSON.stringify({
        query: STOREFRONT_PRODUCT_BY_VARIANT_IDS_QUERY,
        variables: {
          ids: variantIds,
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data.errors) {
      console.error('GraphQL errors:', data.errors);
      return [];
    }

    const results = data.data.nodes
      .filter(node => node && node.id && node.product)
      .map(node => ({
        variantId: node.id,
        product: node.product
      }));
    
    return results;
  } catch (error) {
    console.error('Error fetching product data by variant IDs:', error);
    return [];
  }
}

function getCurrentPrice(product) {
  if (!product || !product.variants || !product.variants.edges.length) {
    return null;
  }

  const allVariants = product.variants.edges.map((edge) => edge.node);

  if (allVariants.length === 0) {
    return null;
  }

  const prices = allVariants
    .map((variant) => {
      const regularPrice = parseFloat(variant.price.amount);
      return regularPrice;
    })
    .filter((price) => !isNaN(price) && price > 0);

  if (prices.length === 0) {
    return null;
  }

  return Math.min(...prices);
}

function formatPrice(price, currencyCode = 'USD') {
  if (price === null || price === undefined) {
    return 'N/A';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
  }).format(price);
}

function hasAnyProductOnSale(products) {
  return products.some((product) => {
    if (!product || !product.variants || !product.variants.edges.length) {
      return false;
    }

    return product.variants.edges.some((edge) => {
      const variant = edge.node;
      const price = parseFloat(variant.price.amount);
      const compareAtPrice = variant.compareAtPrice ? parseFloat(variant.compareAtPrice.amount) : null;

      return compareAtPrice && compareAtPrice > price;
    });
  });
}

function getProductBadge(product) {
  if (!product || !product.metafield) {
    return null;
  }
  const rawValue = product.metafield.value;
  if (typeof rawValue !== 'string') return null;
  return rawValue;
}

function anyChildHasNewBadge(products) {
  return products.some((product) => {
    const badge = getProductBadge(product);
    if (!badge) return false;
    return badge
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .includes('new');
  });
}

function areAllChildProductsOutOfStock(products) {
  if (!products || products.length === 0) {
    return true;
  }

  return products.every((product) => {
    if (!product || !product.variants || !product.variants.edges.length) {
      return true;
    }

    return product.variants.edges.every((edge) => {
      const variant = edge.node;
      const quantityAvailable = variant.quantityAvailable || 0;
      const availableForSale = variant.availableForSale;
      return quantityAvailable <= 0 && !availableForSale;
    });
  });
}

function updateShopNowButton(container, isOutOfStock) {
  const quickAddContainer = container.querySelector('.quick-add');
  if (!quickAddContainer) {
    return;
  }

  const submitButton = quickAddContainer.querySelector('.quick-add__submit');
  if (!submitButton) {
    return;
  }

  if (isOutOfStock) {
    submitButton.innerHTML = '<span>Out of Stock</span>';
    submitButton.disabled = true;
    submitButton.style.pointerEvents = 'none';
    submitButton.style.opacity = '0.6';
    submitButton.removeAttribute('onclick');
  } else {
    const productHandle = quickAddContainer.getAttribute('data-product-handle');
    submitButton.innerHTML = '<span>Shop Now</span>';
    submitButton.disabled = false;
    submitButton.style.pointerEvents = 'all';
    submitButton.style.opacity = '1';
    if (productHandle) {
      submitButton.setAttribute('onclick', `window.location.href='${productHandle}'`);
    }
  }
}

function updateSaleBadge(container, showSale) {
  const badgesContainer = container.querySelector('.c-badges');
  if (!badgesContainer) {
    return;
  }

  const existingSaleBadge = badgesContainer.querySelector('.c-badge--on-sale');

  if (showSale && !existingSaleBadge) {
    const saleBadge = document.createElement('span');
    saleBadge.className = 'c-badge c-badge--on-sale';
    saleBadge.textContent = 'On Sale';
    badgesContainer.appendChild(saleBadge);
  } else if (!showSale && existingSaleBadge) {
    existingSaleBadge.remove();
  }
}

function updateNewBadge(container, showNew) {
  const badgesContainer = container.querySelector('.c-badges');
  if (!badgesContainer) {
    return;
  }

  const existingNewBadge = badgesContainer.querySelector('.c-badge--new');

  if (showNew && !existingNewBadge) {
    const newBadge = document.createElement('span');
    newBadge.className = 'c-badge c-badge--new';
    newBadge.textContent = 'New';
    badgesContainer.appendChild(newBadge);
  } else if (!showNew && existingNewBadge) {
    existingNewBadge.remove();
  }
}

function updateAllBadges(container, badgesArray) {
  const badgesContainer = container.querySelector('.c-badges');
  if (!badgesContainer) {
    return;
  }

  const existingBadges = Array.from(badgesContainer.querySelectorAll('.c-badge:not(.c-badge--on-sale)'));
  const existingBadgeTexts = existingBadges.map(b => b.textContent.trim());
  
  const validBadges = badgesArray.filter(b => b && b.trim() !== '');

  if (validBadges.length === 0 && existingBadges.length === 0) {
    return;
  }

  if (validBadges.length > 0) {
    existingBadges.forEach(badge => {
      const badgeText = badge.textContent.trim();
      const shouldKeep = validBadges.some(b => b.toLowerCase() === badgeText.toLowerCase());
      if (!shouldKeep && badgeText !== '') {
        badge.remove();
      }
    });
  }

  validBadges.forEach(badgeText => {
    const normalizedText = badgeText.trim();
    const alreadyExists = existingBadgeTexts.some(existing => existing.toLowerCase() === normalizedText.toLowerCase());
    
    if (!alreadyExists) {
      const badge = document.createElement('span');
      if (normalizedText.toLowerCase() === 'new') {
        badge.className = 'c-badge c-badge--new';
      } else {
        badge.className = 'c-badge';
      }
      badge.textContent = normalizedText;
      badgesContainer.appendChild(badge);
    }
  });
}

/**
 * Add backorder line in a Klevu Listing pages (search, collections)
 * Product gets backOrderDate/backOrderMessage metafields from GraphQL.
 */
function updateBlockBackorder(block, product) {
  if (!block || !product) {
    return;
  }


  const backOrderDateRaw = product.backOrderDate?.value || '';
  const backOrderMessageRaw = product.backOrderMessage?.value || '';

  const totalInventory = typeof product.totalInventory === 'number' ? product.totalInventory : 0;
  const isOutOfStock = totalInventory <= 0;

  if (!isOutOfStock) {
    const existing = block.querySelector('.card__stock-status');
    if (existing) existing.remove();
    return;
  }

  let dateFormatted = '';
  if (backOrderDateRaw) {
    const raw = String(backOrderDateRaw).trim();
    const match = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (match) {
      const [, y, m, day] = match;
      const year = parseInt(y, 10);
      const month = parseInt(m, 10);
      const dayNum = parseInt(day, 10);
      const dateStr = raw.slice(0, 10);
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      if (dateStr >= todayStr) {
        dateFormatted = `${month}/${String(dayNum).padStart(2, '0')}/${year}`;
      }
    }
  }

  if (!dateFormatted) {
    return;
  }

  const msg = (backOrderMessageRaw || '').toLowerCase();
  const isPreorder = /preorder|pre-order|pre order/.test(msg);
  const label = isPreorder ? 'Preorder' : 'Backordered';
  const lineText = `${label} - Ships ${dateFormatted}`;

  let container = block.querySelector('.card__stock-status');
  // Add below the product title div (card__heading h5)
  const titleDiv = block.querySelector('.card__information .card__heading.h5:not(.card__sku)')
    || block.querySelector('.card__information .card__heading.h5 a[href*="/products/"]')?.closest('.card__heading.h5');

  if (container) {
    const span = container.querySelector('.stock-backorder');
    if (span) span.textContent = lineText;
  } else if (titleDiv) {
    container = document.createElement('div');
    container.className = 'card__stock-status';
    const span = document.createElement('span');
    span.className = 'stock-backorder';
    span.textContent = lineText;
    container.appendChild(span);
    titleDiv.after(container);
  }
}

async function updateCollectionPageBadges() {
  const isCollectionPage = window.location.pathname.includes('/collections/') && !window.location.pathname.includes('/products/');
  if (!isCollectionPage) {
    return;
  }

  const productCards = document.querySelectorAll('.c-custom-card-product, .product-card-wrapper, .card-wrapper');
  if (productCards.length === 0) {
    return;
  }

  const productIdMap = new Map();
  
  productCards.forEach(card => {
    const badgesContainer = card.querySelector('.c-badges');
    if (!badgesContainer) {
      return;
    }

    let productId = null;
    
    const productIdAttr = card.querySelector('[data-product-id]');
    if (productIdAttr) {
      const idValue = productIdAttr.getAttribute('data-product-id');
      if (idValue) {
        productId = `gid://shopify/Product/${idValue}`;
      }
    }
    
    if (!productId) {
      const productLink = card.querySelector('a[href*="/products/"]');
      if (productLink) {
        const href = productLink.getAttribute('href');
        const match = href.match(/\/products\/([^\/\?]+)/);
        if (match) {
        }
      }
    }

    if (!productId) {
      const form = card.querySelector('form');
      if (form) {
        const variantInput = form.querySelector('input[name="id"]');
        if (variantInput && variantInput.value) {
          const productIdFromForm = form.getAttribute('data-product-id') || form.closest('[data-product-id]')?.getAttribute('data-product-id');
          if (productIdFromForm) {
            productId = `gid://shopify/Product/${productIdFromForm}`;
          }
        }
      }
    }

    if (productId) {
      productIdMap.set(productId, card);
    }
  });

  if (productIdMap.size === 0) {
    return;
  }

  const productIds = Array.from(productIdMap.keys());

  try {
    const productDataPromises = productIds.map(id => fetchProductData(id));
    const productDataResults = await Promise.all(productDataPromises);
    
    productDataResults.forEach((product, index) => {
      const productId = productIds[index];
      const card = productIdMap.get(productId);
      
      if (product && product.metafield && product.metafield.value) {
        const badgesValue = product.metafield.value;
        if (typeof badgesValue === 'string') {
          const badgesArray = badgesValue.split(',').map(b => b.trim()).filter(b => b !== '');
          updateAllBadges(card, badgesArray);
        }
      } else {
        updateAllBadges(card, []);
      }
    });
  } catch (error) {
  }
}

// Fetch and display badges for Klevu products (since Klevu doesn't index the badges metafield)
async function updateKlevuProductBadges() {
  try {
    const productBlocks = document.querySelectorAll('.klevuLanding [ku-product-block], .kuResults [ku-product-block], [ku-product-block]');
    
    if (productBlocks.length === 0) {
      return;
    }
    const productIdMap = new Map();
  productBlocks.forEach(block => {
    const badgesContainer = block.querySelector('.c-badges');
    if (!badgesContainer) {
      return;
    }
    
    let productId = null;
    
    const form = block.querySelector('form[id^="quick-add-klevu-"]');
    if (form) {
      const productIdInput = form.querySelector('input[name="product-id"]');
      if (productIdInput && productIdInput.value) {
        productId = `gid://shopify/Product/${productIdInput.value}`;
      }
    }
    
    // For grouped products, fetch the PARENT product by handle, not the child products
    if (!productId) {
      const quickAdd = block.querySelector('[data-children-products]');
      if (quickAdd) {
        const productHandleAttr = quickAdd.getAttribute('data-product-handle');
        if (productHandleAttr) {
          const handleMatch = productHandleAttr.match(/\/products\/([^\/\?]+)/);
          if (handleMatch && handleMatch[1]) {
            const handle = handleMatch[1];
            productId = `handle:${handle}`; 
          }
        } else {
          const childrenProducts = quickAdd.getAttribute('data-children-products');
          if (childrenProducts) {
            const firstProductId = childrenProducts.split(',')[0].trim();
            if (firstProductId && firstProductId.startsWith('gid://shopify/Product/')) {
              productId = firstProductId;
            }
          }
        }
      }
    }
    
    if (!productId) {
      const productLink = block.querySelector('a[href*="/products/"]');
      if (productLink) {
        const href = productLink.getAttribute('href');
        const match = href.match(/\/products\/([^\/\?]+)/);
        if (match) {
          const dataId = block.getAttribute('data-id');
          if (dataId) {
          }
        }
      }
    }
    
    if (productId) {
      productIdMap.set(productId, block);
    }
  });

  if (productIdMap.size === 0) {
    return;
  }

  const productIds = Array.from(productIdMap.keys());

  try {
    const productDataPromises = productIds.map(id => {
      if (id.startsWith('handle:')) {
        const handle = id.substring(7);
        return fetchProductDataByHandle(handle);
      } else {
        return fetchProductData(id);
      }
    });
    const productDataResults = await Promise.all(productDataPromises);
    
    productDataResults.forEach((product, index) => {
      const productId = productIds[index];
      const block = productIdMap.get(productId);
      
      if (product && product.metafield && product.metafield.value) {
        const badgesValue = product.metafield.value;
        if (typeof badgesValue === 'string') {
          const badgesArray = badgesValue.split(',').map(b => b.trim());
          if (block) {
            updateAllBadges(block, badgesArray);
          }
        }
      } else {
        if (block) {
          updateAllBadges(block, []);
        }
      }
      if (block && product) {
        updateBlockBackorder(block, product);
      }
    });
  } catch (error) {
    console.error('Error updating Klevu product badges:', error);
  }
  } catch (error) {
    console.error('Error updating Klevu product badges:', error);
  }
}

async function updateGroupedProductRatingSkus() {
  const groupedBlocks = document.querySelectorAll('[ku-product-block]');
  const updatePromises = [];

  groupedBlocks.forEach(block => {
    const quickAdd = block.querySelector('[data-children-products]');
    if (!quickAdd) return;

    const childProductsValue = quickAdd.getAttribute('data-children-products');
    if (!childProductsValue || childProductsValue.trim() === '') return;

    const ratingDiv = block.querySelector('.ruk_rating_snippet');
    if (!ratingDiv) return;

    const childIds = childProductsValue.split(',').map(id => id.trim()).filter(Boolean);
    if (childIds.length === 0) return;

    const parentSku = ratingDiv.getAttribute('data-sku') || '';

    const promise = Promise.all(childIds.map(id => fetchProductData(id))).then(childProducts => {
      const skuParts = [parentSku];
      childProducts.forEach(child => {
        if (!child) return;
        if (child.handle) skuParts.push(child.handle);
        if (child.variants && child.variants.edges) {
          child.variants.edges.forEach(({ node }) => {
            if (node.sku) skuParts.push(node.sku);
          });
        }
      });
      ratingDiv.setAttribute('data-sku', skuParts.filter(Boolean).join(';'));
    }).catch(() => {});

    updatePromises.push(promise);
  });

  if (updatePromises.length === 0) return;

  await Promise.all(updatePromises);

  if (typeof ratingSnippet === 'function') {
    try {
      const themeVariant =
        window.settings?.theme_variant ||
        document.querySelector('[data-theme-variant]')?.dataset.themeVariant ||
        'mackenzie';
      ratingSnippet('ruk_rating_snippet', {
        store: themeVariant === 'mackenzie' ? 'mackenzie-ltd.' : 'chesapeake-crab-cakes',
        mode: 'default',
        color: '#0E1311',
        linebreak: false,
        text: 'Reviews',
        singularText: 'Review',
        lang: 'en',
        usePolaris: true,
        showEmptyStars: false,
        enableSyndication: true,
      });
    } catch (e) {}
  }
}

window.updateGroupedProductRatingSkus = updateGroupedProductRatingSkus;

async function updateChildProductPrices() {
  const elementsWithChildren = document.querySelectorAll('[data-children-products]');

  for (const element of elementsWithChildren) {
    const childProductsValue = element.getAttribute('data-children-products');

    if (!childProductsValue || childProductsValue.trim() === '') {
      continue;
    }

    const productIds = childProductsValue
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id);

    if (productIds.length === 0) {
      continue;
    }

    try {
      const productDataPromises = productIds.map((id) => fetchProductData(id));
      const productDataResults = await Promise.all(productDataPromises);

      const validProducts = productDataResults.filter((product) => product !== null);

      if (validProducts.length === 0) {
        continue;
      }

      const currentPrices = validProducts.map((product) => getCurrentPrice(product)).filter((price) => price !== null);

      if (currentPrices.length === 0) {
        continue;
      }

      const lowestPrice = Math.min(...currentPrices);
      const currencyCode = validProducts[0]?.priceRange?.minVariantPrice?.currencyCode || 'USD';

      const priceContainer = element.closest('.c-custom-card-product') || element.parentElement;
      const priceElement = priceContainer?.querySelector('.c-custom-card-product__starting-price');

      if (priceElement) {
        priceElement.textContent = formatPrice(lowestPrice, currencyCode);
      }

      const isOnSale = hasAnyProductOnSale(validProducts);
      updateSaleBadge(priceContainer, isOnSale);

      const hasNew = anyChildHasNewBadge(validProducts);
      updateNewBadge(priceContainer, hasNew);

      const allOutOfStock = areAllChildProductsOutOfStock(validProducts);
      updateShopNowButton(priceContainer, allOutOfStock);

      const allInventoryDepleted = validProducts.every(p => (typeof p.totalInventory === 'number' ? p.totalInventory : 1) <= 0);
      if (allInventoryDepleted) {
        const today = new Date();
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        let bestChild = null;
        let bestDateStr = null;
        for (const child of validProducts) {
          const raw = String(child.backOrderDate?.value || '').trim();
          const dateStr = raw.slice(0, 10);
          if (dateStr >= todayStr && raw.match(/^\d{4}-\d{1,2}-\d{1,2}/)) {
            if (!bestDateStr || dateStr < bestDateStr) {
              bestDateStr = dateStr;
              bestChild = child;
            }
          }
        }
        if (bestChild) {
          updateBlockBackorder(priceContainer, { ...bestChild, totalInventory: 0 });
        } else {
          const existing = priceContainer?.querySelector('.card__stock-status');
          if (existing) existing.remove();
        }
      } else {
        const existing = priceContainer?.querySelector('.card__stock-status');
        if (existing) existing.remove();
      }

    } catch (error) {
      console.error('Error processing child products:', error);
    }
  }
}

function toggleStartingAtLabels() {
  const elementsWithChildren = document.querySelectorAll('[data-children-products]');

  elementsWithChildren.forEach((element) => {
    const childProductsValue = element.getAttribute('data-children-products');
    const hasChildren = childProductsValue && childProductsValue.trim() !== '';

    const priceContainer =
      element.closest('.c-custom-card-product, .product-card, [class*="product"]') || element.parentElement;
    const startingFromContainer = priceContainer?.querySelector('.c-custom-card-product__starting-from');

    if (startingFromContainer) {
      if (hasChildren) {
        startingFromContainer.style.display = 'block';
      } else {
        startingFromContainer.style.display = 'none';
      }
    }
  });
}

function cleanupChildProductPricingState() {
  currentPricingHandlers.forEach((handler) => {
    if (handler.element && handler.element.parentNode) {
      const newElement = handler.element.cloneNode(true);
      handler.element.parentNode.replaceChild(newElement, handler.element);
    }
  });

  currentPricingHandlers = [];

  const saleBadges = document.querySelectorAll('.c-badge--on-sale');
  saleBadges.forEach((badge) => {
    const productCard = badge.closest('.c-custom-card-product, .product-card, [class*="product"]');
    if (productCard && productCard.querySelector('[data-children-products]')) {
      badge.remove();
    }
  });
}

function getCurrentContainerClassState(container) {
  return container ? container.className : '';
}

function getCurrentDropdownLabelValue() {
  const dropdownLabel = document.querySelector('.kuSearchResultsPageContainer .kuDropdown .kuDropdownLabel');
  return dropdownLabel ? dropdownLabel.textContent.trim() : '';
}

function setupPricingMutationObserver() {
  let targetContainer = document.querySelector('.kuSearchResultsPageContainer');

  if (!targetContainer) {
    targetContainer = document.body;
  }

  lastContainerClassState = getCurrentContainerClassState(targetContainer);
  lastDropdownLabelValue = getCurrentDropdownLabelValue();

  if (pricingMutationObserver) {
    pricingMutationObserver.disconnect();
  }

  pricingMutationObserver = new MutationObserver(function (mutations) {
    let shouldReinitializePricing = false;
    let containerClassChanged = false;
    let dropdownLabelChanged = false;
    let newChildProductsFound = false;

    const searchResultsContainer = document.querySelector('.kuSearchResultsPageContainer');
    if (searchResultsContainer && targetContainer !== searchResultsContainer) {
      pricingMutationObserver.disconnect();
      targetContainer = searchResultsContainer;
      lastContainerClassState = getCurrentContainerClassState(targetContainer);
      setupPricingMutationObserver();
      return;
    }

    mutations.forEach(function (mutation) {
      if (mutation.type === 'attributes' && mutation.target === targetContainer && mutation.attributeName === 'class') {
        const currentClassState = getCurrentContainerClassState(targetContainer);
        if (currentClassState !== lastContainerClassState) {
          containerClassChanged = true;
          lastContainerClassState = currentClassState;
        }
      }

      if (mutation.type === 'childList' || mutation.type === 'characterData') {
        const currentDropdownValue = getCurrentDropdownLabelValue();
        if (currentDropdownValue !== lastDropdownLabelValue) {
          dropdownLabelChanged = true;
          lastDropdownLabelValue = currentDropdownValue;
        }
      }

      if (mutation.target.matches && mutation.target.matches('.kuDropdownLabel')) {
        const currentDropdownValue = getCurrentDropdownLabelValue();
        if (currentDropdownValue !== lastDropdownLabelValue) {
          dropdownLabelChanged = true;
          lastDropdownLabelValue = currentDropdownValue;
        }
      }

      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const hasChildProducts = node.querySelector && node.querySelector('[data-children-products]');
            const isProductContainer =
              node.matches &&
              (node.matches('.product-card, .c-custom-card-product, [class*="product"]') ||
                node.querySelector('.product-card, .c-custom-card-product, [class*="product"]'));

            if (hasChildProducts || isProductContainer) {
              shouldReinitializePricing = true;

              if (!hasInitializedPricing && hasChildProducts) {
                newChildProductsFound = true;
              }
            }
          }
        });
      }

      if (mutation.type === 'attributes' && mutation.target !== targetContainer) {
        const target = mutation.target;
        if (target.hasAttribute && target.hasAttribute('data-children-products')) {
          shouldReinitializePricing = true;
        }
      }
    });

    if (containerClassChanged || shouldReinitializePricing || dropdownLabelChanged || newChildProductsFound) {
      clearTimeout(window.pricingReinitTimeout);

      let delay = 500;
      if (containerClassChanged) delay = 1000;
      if (dropdownLabelChanged) delay = 800;
      if (newChildProductsFound) delay = 100;

      window.pricingReinitTimeout = setTimeout(function () {
        cleanupChildProductPricingState();
        initializeChildProductPricing();
      }, delay);
    }
  });

  pricingMutationObserver.observe(targetContainer, {
    childList: true,
    subtree: true,
    attributes: true,
    characterData: true,
    attributeFilter: ['data-children-products', 'class'],
  });

  const dropdownLabel = document.querySelector('.kuSearchResultsPageContainer .kuDropdown .kuDropdownLabel');
  if (dropdownLabel) {
    const dropdownObserver = new MutationObserver(function (mutations) {
      const currentDropdownValue = getCurrentDropdownLabelValue();
      if (currentDropdownValue !== lastDropdownLabelValue) {
        lastDropdownLabelValue = currentDropdownValue;

        clearTimeout(window.pricingReinitTimeout);
        window.pricingReinitTimeout = setTimeout(function () {
          cleanupChildProductPricingState();
          initializeChildProductPricing();
        }, 800);
      }
    });

    dropdownObserver.observe(dropdownLabel, {
      childList: true,
      characterData: true,
      subtree: true,
      attributes: true,
    });
  }
}

async function initializeChildProductPricing() {
  hasInitializedPricing = true;
  toggleStartingAtLabels();
  await updateChildProductPrices();
  await updateKlevuProductBadges();
}

function checkForChildProductElements() {
  const elementsWithChildren = document.querySelectorAll('[data-children-products]');

  if (elementsWithChildren.length > 0) {
    clearInterval(initCheckInterval);
    initializeChildProductPricing();
    return true;
  }

  return false;
}

function setupKlevuBadgeListener() {
  if (typeof klevu === 'undefined') {
    // Klevu not loaded yet, try again
    setTimeout(setupKlevuBadgeListener, 500);
    return;
  }

  if (window._klevuBadgeListenerAttached) {
    return;  
  }
  window._klevuBadgeListenerAttached = true;

  klevu.afterTemplateRender('landing, catnav', function (data, scope) {
    setTimeout(() => {
      updateKlevuProductBadges();
      updateGroupedProductRatingSkus();
    }, 300);
  });
}

let preloadedBadgeData = null;
let preloadPromise = null;

async function preloadPDPBadgeData() {
  const isPDP = window.location.pathname.includes('/products/');
  
  if (!isPDP || preloadPromise) {
    return preloadPromise;
  }

  preloadPromise = (async () => {
    try {
      await new Promise(resolve => {
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', resolve, { once: true });
        } else {
          setTimeout(resolve, 0);
        }
      });

      const childProductElements = document.querySelectorAll('.c-grouped-products__product');
      
      if (childProductElements.length === 0) {
        return null;
      }

      const variantIds = [];
      
      childProductElements.forEach((childElement) => {
        const variantId = childElement.getAttribute('data-product-id');
        
        if (variantId) {
          let gid;
          if (variantId.startsWith('gid://shopify/ProductVariant/')) {
            gid = variantId;
          } else {
            gid = `gid://shopify/ProductVariant/${variantId}`;
          }
          variantIds.push(gid);
        }
      });

      if (variantIds.length === 0) {
        return null;
      }

      const childProducts = await fetchProductDataByVariantIds(variantIds);
      preloadedBadgeData = childProducts;
      return childProducts;
    } catch (error) {
      console.error('Error preloading badge data:', error);
      return null;
    }
  })();

  return preloadPromise;
}

async function updatePDPGroupedProductBadges() {
  const isPDP = window.location.pathname.includes('/products/');
  
  if (!isPDP) {
    return;
  }

  const childProductElements = document.querySelectorAll('.c-grouped-products__product');
  
  if (childProductElements.length === 0) {
    const parentMediaWrapper = document.querySelector('.product__media-wrapper');
    if (parentMediaWrapper) {
      const parentBadgesContainer = parentMediaWrapper.querySelector('.c-badges');
      if (parentBadgesContainer && parentBadgesContainer.hasAttribute('data-published-at')) {
        return;
      }
    }
    return;
  }

  const variantIds = [];
  const variantToElementMap = new Map();
  
  childProductElements.forEach((childElement, index) => {
    const variantId = childElement.getAttribute('data-product-id');
    
    if (variantId) {
      let gid;
      if (variantId.startsWith('gid://shopify/ProductVariant/')) {
        gid = variantId;
      } else {
        gid = `gid://shopify/ProductVariant/${variantId}`;
      }
      variantIds.push(gid);
      variantToElementMap.set(gid, childElement);
    }
  });

  if (variantIds.length === 0) {
    return;
  }

  try {
    let childProducts;
    if (preloadedBadgeData) {
      childProducts = preloadedBadgeData;
    } else if (preloadPromise) {
      childProducts = await preloadPromise;
    } else {
      childProducts = await fetchProductDataByVariantIds(variantIds);
    }
    
    if (!childProducts || childProducts.length === 0) {
      return;
    }
    
    const allBadgesSet = new Set();
    
    childProducts.forEach(({ variantId, product }, index) => {
      if (!product) {
        return;
      }
      
      const childElement = variantToElementMap.get(variantId);
      
      if (product.metafield && product.metafield.value) {
        const badgesValue = product.metafield.value;
        if (typeof badgesValue === 'string') {
          const badgesArray = badgesValue.split(',').map(b => b.trim()).filter(b => b !== '');
          badgesArray.forEach(badge => allBadgesSet.add(badge));
        }
      }  
  });

    const parentMediaWrapper = document.querySelector('.product__media-wrapper');
    
    if (parentMediaWrapper && allBadgesSet.size > 0) {
      const allBadgesArray = Array.from(allBadgesSet);
      updateAllBadges(parentMediaWrapper, allBadgesArray);
    }  
    
  } catch (error) {
    console.error('Error updating PDP grouped product badges:', error);
  }
}

function cleanupIncorrectNewBadges() {
  const isCollectionPage = window.location.pathname.includes('/collections/') && !window.location.pathname.includes('/products/');
  if (!isCollectionPage) {
    return;
  }
  
  const badgeContainers = document.querySelectorAll('.c-badges');
  
  badgeContainers.forEach(container => {
    const newBadge = container.querySelector('.c-badge--new');
    if (!newBadge) {
      return;
    }
    
    const productCard = container.closest('.c-custom-card-product, .product-card-wrapper, .card-wrapper');
    if (!productCard) {
      return;
    }
    
  });
}

function startInitializationCheck() {
  const isCollectionPage = window.location.pathname.includes('/collections/') && !window.location.pathname.includes('/products/');
  const isPDP = window.location.pathname.includes('/products/');
  
  setupPricingMutationObserver();

  setupKlevuBadgeListener();

  const runBadgeUpdates = () => {
    updateKlevuProductBadges();
    updateCollectionPageBadges();
    updateGroupedProductRatingSkus();
    if (isPDP) {
      updatePDPGroupedProductBadges();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      runBadgeUpdates();
      requestAnimationFrame(runBadgeUpdates);
    });
  } else {
    runBadgeUpdates();
    requestAnimationFrame(runBadgeUpdates);
  }
  setTimeout(() => {
    updateCollectionPageBadges();
    if (isPDP) {
      updatePDPGroupedProductBadges();
    }
  }, 200);
  
  setTimeout(() => {
    updateCollectionPageBadges();
    if (isPDP) {
      updatePDPGroupedProductBadges();
    }
  }, 800);

  if (isCollectionPage) {
    setTimeout(() => {
      cleanupIncorrectNewBadges();
    }, 2000);
  }

  if (checkForChildProductElements()) {
    return;
  }

  let checkCount = 0;
  const maxChecks = 10;
  initCheckInterval = setInterval(() => {
    checkCount++;

    if (checkForChildProductElements()) {
      return;
    }

    if (checkCount >= maxChecks) {
      clearInterval(initCheckInterval);
    }
  }, 500);
}

const currentPath = window.location.pathname;
const isPDP = currentPath.includes('/products/');
const isCollection = currentPath.includes('/collections/') && !isPDP;

if (isPDP) {
  preloadPDPBadgeData();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startInitializationCheck);
} else {
  startInitializationCheck();
}
